// EL MÉDICO DEL DIRECTORIO DEL MERCADO (Lissette 2026-10-15: "también puedes
// agregar Dr. en el buscador, Dr. especialista, así como está en el Mercado").
//
// Dos usos del mismo buscador (`components/BuscadorDeMedico.jsx`):
//   · en la ficha (Médicos y citas → + Médico), elegir uno llena el formulario;
//   · en el cotizador del Mercado, cada plan dice si ese médico está en su red.
// La red se pregunta por NPI, así que solo se revisan los médicos que lo tienen.

/** "3055551234" → "(305) 555-1234". Lo que no tenga 10 dígitos, tal cual. */
export function telefonoBonito(t) {
  const d = String(t || '').replace(/\D/g, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(t || '');
}

/**
 * La dirección en una línea: calle, ciudad, estado ZIP. Sirve igual para un
 * médico del directorio y para uno de la ficha (mismas claves); uno guardado
 * antes del 2026-10-25, con todo en `direccion`, sale tal cual.
 */
export function direccionDelMedico(m) {
  if (!m) return '';
  const cola = [m.estado, m.zip].filter(Boolean).join(' ');
  return [m.direccion, m.ciudad, cola].filter(Boolean).join(', ');
}

/**
 * Elegir un médico del directorio en el formulario de la ficha: pone lo que
 * el directorio sabe y deja lo demás (red, centro, fax, notas) como estaba.
 * `origen` dice de dónde salió, que es la costumbre buena de la ficha.
 */
export function llenarConMedicoDelDirectorio(form, m, hoy = new Date()) {
  const fecha = `${String(hoy.getMonth() + 1).padStart(2, '0')}/${String(hoy.getDate()).padStart(2, '0')}/${hoy.getFullYear()}`;
  return {
    ...form,
    nombre: m.nombre || form.nombre,
    especialidad: (m.especialidades && m.especialidades[0]) || form.especialidad,
    npi: m.npi || form.npi,
    // Calle, ciudad, estado y ZIP en su casilla (Lissette 2026-10-25). Si el
    // directorio no trae la calle, la dirección que había se queda entera.
    direccion: m.direccion || form.direccion,
    ciudad: m.direccion ? (m.ciudad || '') : form.ciudad,
    estado: m.direccion ? (m.estado || '') : form.estado,
    zip: m.direccion ? (m.zip || '') : form.zip,
    telefono: m.telefono ? telefonoBonito(m.telefono) : form.telefono,
    origen: `Directorio del Mercado de Salud (HealthCare.gov), ${fecha}`,
  };
}

/** De una lista de médicos, los que se pueden revisar (NPI de 10 dígitos, sin repetir). */
export function medicosRevisables(lista) {
  const vistos = new Set();
  const revisables = [];
  let sinNpi = 0;
  for (const m of lista || []) {
    const npi = String((m && m.npi) || '').replace(/\D/g, '');
    if (npi.length !== 10) { sinNpi += 1; continue; }
    if (vistos.has(npi)) continue;
    vistos.add(npi);
    revisables.push({ npi, nombre: (m && m.nombre) || npi });
  }
  return { revisables, sinNpi };
}

/** Lo que dice UN plan de los médicos elegidos. Sin respuesta = "sin dato". */
export function resumenDeRed(planId, red, medicos) {
  const delPlan = (red && red[planId]) || {};
  const r = { enRed: [], fuera: [], sinDato: [] };
  for (const m of medicos || []) {
    const s = delPlan[m.npi];
    if (s === 'en_red') r.enRed.push(m);
    else if (s === 'fuera') r.fuera.push(m);
    else r.sinDato.push(m);
  }
  r.todos = (medicos || []).length > 0 && !r.fuera.length && !r.sinDato.length;
  return r;
}

/**
 * Por qué el Mercado no contestó la revisión de médicos o medicamentos (E-419).
 * Antes era siempre la misma frase, y así no había forma de saber si era la
 * llave del Mercado, un rechazo de CMS o que no respondía.
 */
export function motivoDelMercado(err, es = true) {
  const code = String(err?.code || '');
  if (code === 'marketplace_key_invalid') {
    return es ? 'la conexión de Deskte con el Mercado venció (avísanos por soporte)' : "Deskte's Marketplace connection expired (contact support)";
  }
  if (code === 'not_configured') return es ? 'el Mercado no está conectado en Deskte' : 'the Marketplace is not connected in Deskte';
  if (err?.cmsStatus) return es ? `el Mercado contestó con error ${err.cmsStatus}` : `the Marketplace answered with error ${err.cmsStatus}`;
  if (code === 'Invalid request') return es ? 'la pregunta no tenía el formato esperado' : 'the request was not in the expected format';
  return es ? 'el Mercado no respondió' : 'the Marketplace did not respond';
}
